import {
  AlertCard,
  Drawer,
  FormField,
  MonthCalendar,
  SegmentedControl,
} from "@caprikorn/dynamo-ui";
import {
  addMonths,
  getFirstDayOfMonth,
  getLastDayOfMonth,
} from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { Download } from "@icons";
import { usePostExportDataRequest } from "@services/apiEcms/usePostExportDataRequest";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./DataExportDrawer.module.css";
import type {
  DataExportDrawerProps,
  DataExportForm,
} from "./DataExportDrawer.types";

const MAX_MONTH_RANGE = 6;

export const DataExportDrawer: FC<DataExportDrawerProps> = ({
  onHide,
  isProcessing,
  perimeterExported,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const { mutate, isPending } = usePostExportDataRequest();
  const { handleSubmit, setValue, trigger, control } = useForm<DataExportForm>({
    defaultValues: {
      dateRange: { from: undefined, to: undefined },
      // TODO: Delete dataType and dataFormat when data is fixed form FLUX/BILLING
      dataType: "INVOICE",
      dataFormat: "PDF",
    },
  });

  const dataType = useWatch({ control, name: "dataType" });
  const formId = useId();

  return (
    <Drawer onClose={onHide}>
      <Drawer.Header title={t("perimeter.dataExportDrawer.title")} />
      <Drawer.Body>
        <form
          className={classes.form}
          id={formId}
          onSubmit={handleSubmit((data) => {
            const isMonthCurrent =
              new Date().getMonth() + 1 === Number(data.dateRange.to.slice(5));

            mutate({
              fileType: data.dataFormat,
              type: data.dataType,
              dateRange: {
                from: getFirstDayOfMonth(data.dateRange.from),
                to: isMonthCurrent
                  ? `${data.dateRange.to}-${new Date().getDate()}`
                  : getLastDayOfMonth(data.dateRange.to),
              },
              perimeter: {
                customers: perimeterExported,
              },
            });
          })}
        >
          {/* TODO: Delete AlertCard when data is fixed form FLUX/BILLING */}
          <AlertCard size="sm" variant="error">
            {t("perimeter.dataExportDrawer.disabledWarning")}
          </AlertCard>
          <Controller
            control={control}
            name="dataType"
            render={({ field, fieldState }) => (
              <FormField
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("perimeter.dataExportDrawer.dataTypeLabel")}
              >
                <SegmentedControl
                  onBlur={field.onBlur}
                  onChange={async (value) => {
                    field.onChange(value);

                    if (value === "CONSUMPTION") {
                      setValue("dataFormat", "CSV"); // Only CSV is allowed for CONSUMPTION
                      await trigger("dataFormat");
                    }
                  }}
                >
                  <SegmentedControl.Item
                    disabled
                    isSelected={field.value === "CONSUMPTION"}
                    label={t(
                      "perimeter.dataExportDrawer.dataTypeOption.consumption",
                    )}
                    value="CONSUMPTION"
                  />
                  <SegmentedControl.Item
                    isSelected={field.value === "INVOICE"}
                    label={t(
                      "perimeter.dataExportDrawer.dataTypeOption.invoice",
                    )}
                    value="INVOICE"
                  />
                </SegmentedControl>
              </FormField>
            )}
            rules={{ required: "form.error.required" }}
          />

          <Controller
            control={control}
            name="dataFormat"
            render={({ field, fieldState }) => (
              <FormField
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("perimeter.dataExportDrawer.dataFormatLabel")}
              >
                <SegmentedControl
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                >
                  <SegmentedControl.Item
                    disabled
                    isSelected={field.value === "CSV"}
                    label={t("fileExtension.csv", { ns: "common" })}
                    value="CSV"
                  />
                  <SegmentedControl.Item
                    disabled={dataType === "CONSUMPTION"}
                    isSelected={field.value === "PDF"}
                    label={t("fileExtension.pdf", { ns: "common" })}
                    value="PDF"
                  />
                </SegmentedControl>
              </FormField>
            )}
            rules={{ required: "form.error.required" }}
          />
          <AlertCard size="sm" variant="warning">
            {t("perimeter.dataExportDrawer.monthRangeWarning", {
              months: MAX_MONTH_RANGE,
            })}
          </AlertCard>

          <Controller
            control={control}
            name="dateRange"
            render={({ field, fieldState }) => (
              <FormField
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("perimeter.dataExportDrawer.dateRangeLabel")}
              >
                <MonthCalendar
                  disabledMonth={(month) => {
                    if (!field.value?.from) return false;

                    return (
                      `${month}-01` >
                        addMonths(
                          `${field.value.from}-01`,
                          MAX_MONTH_RANGE - 1,
                        ) ||
                      `${month}-01` <
                        addMonths(
                          `${field.value.from}-01`,
                          -MAX_MONTH_RANGE + 1,
                        )
                    );
                  }}
                  isRange
                  maxValue={new Date().toISOString()}
                  onBlur={field.onBlur}
                  onChange={(value) =>
                    field.onChange({ from: value[0], to: value[1] })
                  }
                  value={[field.value.from, field.value.to]}
                />
              </FormField>
            )}
            rules={{
              validate: {
                required: (value) => {
                  if (!value.from || !value.to) {
                    return "form.error.required";
                  }

                  return true;
                },
              },
            }}
          />
        </form>
      </Drawer.Body>
      <Drawer.Footer>
        <Drawer.Footer.ConfirmButton
          data-testid="ExportModalForm-submit"
          form={formId}
          isLoading={isProcessing || isPending}
          startIcon={Download}
          type="submit"
        >
          {t("perimeter.dataExportDrawer.exportButton")}
        </Drawer.Footer.ConfirmButton>
      </Drawer.Footer>
    </Drawer>
  );
};
